/** Deterministic operational UX helpers — no invented scores. */

export type PressureBand = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL'

export type StabilityBand = 'STABLE' | 'WATCH' | 'AT_RISK' | 'DEGRADED' | 'CRITICAL'

export function pressureBand(value?: number | null): PressureBand {
  const v = Number(value ?? 0)
  if (v >= 1.5) return 'CRITICAL'
  if (v >= 0.9) return 'HIGH'
  if (v >= 0.4) return 'MODERATE'
  return 'LOW'
}

export function normalizeStability(level?: string | null): StabilityBand {
  const u = String(level || 'STABLE').toUpperCase()
  if (u === 'CRITICAL' || u === 'AT_RISK' || u === 'DEGRADED' || u === 'WATCH' || u === 'STABLE') {
    return u as StabilityBand
  }
  if (u === 'DECLINING') return 'DEGRADED'
  if (u === 'IMPROVING') return 'STABLE'
  return 'STABLE'
}

export function stabilityColor(band: StabilityBand): string {
  switch (band) {
    case 'CRITICAL':
    case 'AT_RISK':
      return '#f87171'
    case 'DEGRADED':
      return '#fbbf24'
    case 'WATCH':
      return '#94a3b8'
    default:
      return '#34d399'
  }
}

export function pressureColor(band: PressureBand): string {
  switch (band) {
    case 'CRITICAL':
      return '#ef4444'
    case 'HIGH':
      return '#f97316'
    case 'MODERATE':
      return '#eab308'
    default:
      return '#64748b'
  }
}

export function formatRelativeTime(iso?: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return '—'
  const diffMs = Date.now() - then
  const mins = Math.floor(diffMs / 60000)
  if (mins < 60) return `${Math.max(1, mins)}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 48) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function formatPercentOrMissing(
  value: number | null | undefined,
  missingLabel: string,
): string {
  if (value == null || !Number.isFinite(Number(value))) return missingLabel
  return `${Math.round(Number(value))}%`
}

/** Map pressure band to EnterpriseOperationalUx SCSS module class. */
export function pressureBadgeClass(
  band: PressureBand | string | null | undefined,
  styles: Record<string, string>,
): string {
  const b = String(band || 'LOW').toUpperCase()
  if (b === 'CRITICAL') return styles.badgePressureCritical
  if (b === 'HIGH') return styles.badgePressureHigh
  if (b === 'MODERATE') return styles.badgePressureMod
  return styles.badgePressureLow
}

export function severityRank(severity?: string | null): number {
  const s = String(severity || 'INFO').toUpperCase()
  if (s === 'CRITICAL') return 4
  if (s === 'HIGH') return 3
  if (s === 'WARNING' || s === 'MEDIUM' || s === 'MODERATE') return 2
  if (s === 'LOW') return 1
  return 0
}
